import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from './button'
import { Input } from './input'
import { cn } from '../lib/utils'

/**
 * Pengganti confirm()/prompt() bawaan browser (aturan #8 CLAUDE.md).
 *
 * Pasang <ConfirmProvider> sekali di akar SPA, lalu:
 *   const confirm = useConfirm()
 *   if (await confirm({ title: 'Delete design?', destructive: true })) { ... }
 *
 *   const prompt = usePrompt()
 *   const name = await prompt({ title: 'Rename', defaultValue: old })  // null = batal
 *
 * Hanya satu dialog terbuka pada satu waktu; permintaan kedua saat dialog
 * pertama masih terbuka membatalkan yang pertama (resolve false/null), supaya
 * Promise yang menggantung tidak pernah ada.
 */

export interface ConfirmOptions {
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Tombol konfirmasi merah, untuk aksi yang menghapus atau tidak bisa diulang. */
  destructive?: boolean
}

export interface PromptOptions extends Omit<ConfirmOptions, 'destructive'> {
  label?: string
  placeholder?: string
  defaultValue?: string
  /** Kembalikan pesan error untuk menolak nilai; undefined = sah. */
  validate?: (value: string) => string | undefined
}

export type ConfirmPending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (ok: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (value: string | null) => void }

interface ConfirmApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  prompt: (opts: PromptOptions) => Promise<string | null>
}

const ConfirmContext = React.createContext<ConfirmApi | null>(null)

function batalkan(p: ConfirmPending) {
  if (p.kind === 'confirm') p.resolve(false)
  else p.resolve(null)
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<ConfirmPending | null>(null)
  const aktif = React.useRef<ConfirmPending | null>(null)

  const buka = React.useCallback((p: ConfirmPending) => {
    if (aktif.current) batalkan(aktif.current)
    aktif.current = p
    setPending(p)
  }, [])

  const tutup = React.useCallback(() => {
    aktif.current = null
    setPending(null)
  }, [])

  const api = React.useMemo<ConfirmApi>(
    () => ({
      confirm: (opts) => new Promise((resolve) => buka({ kind: 'confirm', opts, resolve })),
      prompt: (opts) => new Promise((resolve) => buka({ kind: 'prompt', opts, resolve })),
    }),
    [buka],
  )

  // Provider dilepas (misalnya logout) saat dialog terbuka: jangan biarkan
  // pemanggilnya menunggu selamanya.
  React.useEffect(() => () => {
    if (aktif.current) batalkan(aktif.current)
  }, [])

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {pending && (
        <ConfirmModal
          key={pending.kind + pending.opts.title}
          pending={pending}
          onDone={(hasil) => {
            if (pending.kind === 'confirm') pending.resolve(hasil === true)
            else pending.resolve(typeof hasil === 'string' ? hasil : null)
            tutup()
          }}
        />
      )}
    </ConfirmContext.Provider>
  )
}

function useApi(): ConfirmApi {
  const api = React.useContext(ConfirmContext)
  if (!api) throw new Error('useConfirm/usePrompt butuh <ConfirmProvider> di atasnya')
  return api
}

export function useConfirm() {
  return useApi().confirm
}

export function usePrompt() {
  return useApi().prompt
}

/**
 * Tampilan dialognya. Diekspor terpisah supaya halaman yang butuh dialog
 * terkendali (misalnya menunggu request sebelum menutup) bisa memakainya
 * langsung tanpa provider.
 */
export function ConfirmModal({
  pending,
  onDone,
}: {
  pending: ConfirmPending
  /** true/false untuk confirm, string/null untuk prompt. */
  onDone: (result: boolean | string | null) => void
}) {
  const { opts } = pending
  const isPrompt = pending.kind === 'prompt'
  const promptOpts = isPrompt ? (opts as PromptOptions) : null
  const [value, setValue] = React.useState(promptOpts?.defaultValue ?? '')
  const [error, setError] = React.useState<string | undefined>()
  const destructive = !isPrompt && (opts as ConfirmOptions).destructive

  const kirim = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!isPrompt) return onDone(true)
    const err = promptOpts?.validate?.(value)
    if (err) return setError(err)
    onDone(value)
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onDone(isPrompt ? null : false)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border border-border bg-card p-6 text-card-foreground shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
          // Untuk confirm, fokus awal di tombol Cancel: Enter tanpa sengaja
          // tidak boleh menjalankan aksi yang merusak.
          onOpenAutoFocus={(e) => {
            if (isPrompt) return
            e.preventDefault()
            ;(e.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-cancel]')?.focus()
          }}
        >
          <form onSubmit={kirim} className="space-y-4">
            <div className="space-y-1.5">
              <Dialog.Title className="text-lg font-semibold leading-none tracking-tight">
                {opts.title}
              </Dialog.Title>
              {opts.description ? (
                <Dialog.Description className="text-sm text-muted-foreground">
                  {opts.description}
                </Dialog.Description>
              ) : (
                // Radix memperingatkan dialog tanpa deskripsi; kosongkan secara eksplisit.
                <Dialog.Description className="sr-only">{opts.title}</Dialog.Description>
              )}
            </div>

            {promptOpts && (
              <div className="space-y-1.5">
                {promptOpts.label && (
                  <label htmlFor="confirm-prompt" className="text-sm font-medium">
                    {promptOpts.label}
                  </label>
                )}
                <Input
                  id="confirm-prompt"
                  autoFocus
                  value={value}
                  placeholder={promptOpts.placeholder}
                  onChange={(e) => {
                    setValue(e.target.value)
                    setError(undefined)
                  }}
                  aria-invalid={error ? true : undefined}
                />
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                data-cancel
                onClick={() => onDone(isPrompt ? null : false)}
              >
                {opts.cancelLabel ?? 'Cancel'}
              </Button>
              <Button type="submit" variant={destructive ? 'destructive' : 'default'}>
                {opts.confirmLabel ?? (isPrompt ? 'Save' : 'Confirm')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
